/**
 * **로그인 실패를 폼이 무엇이라고 말하는가 한 곳** — 의존성이 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 로그인 요청은 인터셉터 토스트를 끄고(`showErrorMessage:false`) **폼 한 곳이 말한다**. 종전에는 토스트가
 * 서버 문장(`Invalid credentials` 같은 영문 상태 이름)을, 폼이 고정 문구를 **함께** 띄워 화면이 두 말을 했다.
 * 비활성 콜팝 조직의 로그인 거부(R-A §1-4)는 서버가 사람에게 할 한국어 문장을 주는데, 폼이 그 위에
 * 「아이디, 비밀번호를 정확히 입력 하십시오.」를 얹으면 거짓이 된다 — 비밀번호는 맞았다.
 *
 * 로그인 실패 봉투는 `401 {error}` 이고, **사유 코드가 있는 거부만 `{error, code}`** 다(appserver 보고서 §4-4 —
 * 지금은 `CALLPOP_ORG_INACTIVE` 하나). 코드는 「이 `error` 는 사람에게 보일 문장이다」라는 서버의 선언이다.
 */
// 확장자를 적는 이유: 단위 테스트가 앱 번들 밖(node)에서 읽는다 — node ESM 은 상대 경로 확장자를 추론하지 않는다.
import {
  NETWORK_ERROR_MESSAGE,
  httpErrorBody,
  httpErrorMessage,
  httpStatusOf,
  isTransportError,
} from '../plugins/http/policy.js'

/**
 * 폼에 앉힐 문장 — 말할 것이 없으면 빈 문자열이다.
 *
 * 1. **전송 오류** → 네트워크 문장(토스트를 껐으므로 폼이 대신 말한다)
 * 2. **사유 코드가 있는 거부** → 서버 `error` 문장 그대로(접두 없음)
 * 3. **자격 증명을 보낸 입구의 401** → [credentialsText](종전 고정 문구)
 * 4. 그 밖의 HTTP 실패 → 서버 문장(종전 토스트가 하던 말). 토큰 입구의 401 도 여기다 — 사람이 넣은 아이디·
 *    비밀번호가 없으니 「아이디, 비밀번호」를 말할 자리가 아니다
 * 5. HTTP 가 아닌 실패(스크립트 오류) → 빈 문자열(폼이 할 말이 아니다)
 *
 * @param {*} error 로그인 요청이 던진 것
 * @param {?string} credentialsText 아이디·비밀번호 입구의 고정 문구(번역된 것). 토큰 입구는 주지 않는다
 * @returns {string}
 */
export function loginFailureText(error, credentialsText) {
  if (isTransportError(error)) return NETWORK_ERROR_MESSAGE

  const status = httpStatusOf(error)
  if (status == null) return ''

  const body = httpErrorBody(error)
  const reason = body && hasText(body.code) && hasText(body.error) ? body.error.trim() : ''
  if (reason) return reason
  if (status === 401 && hasText(credentialsText)) return credentialsText

  return httpErrorMessage(error, hasText(credentialsText) ? credentialsText : undefined)
}

function hasText(value) {
  return typeof value === 'string' && value.trim() !== ''
}
